import React, { useMemo } from 'react';
import { Chip, TableCell } from '@mui/material';
import { useRecordContext } from 'components/record/useRecordContext';
import { Link } from 'react-router-dom';
import { RecordType } from 'components/record/RecordContext';
import dayjs from 'dayjs';
import { Check } from '@mui/icons-material';
import EventStatusBadge from 'components/admin/EventStatusBadge';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getAttributeFromObject = (object: any, attributes: string) => {
  const attributesList = attributes.split('.');
  let obj = object;
  for (const attr of attributesList) {
    if (!obj[attr]) {
      return '';
    }
    obj = obj[attr];
  }
  return obj;
};

export const ListField = (props: FieldProps) => {
  const { source, transform } = props;
  const { record } = useRecordContext();
  const formatted = useMemo(() => {
    if (typeof transform === 'function') {
      return transform(getAttributeFromObject(record, source));
    }
    return getAttributeFromObject(record, source);
  }, [record, transform, source]);
  return <TableCell>{formatted}</TableCell>;
};

export type FieldProps = {
  transform?: (field: string) => JSX.Element;
  source: string;
  label?: string;
  width?: string;
  hideOnMobile?: boolean;
  defaultHidden?: boolean;
  hideable?: boolean;
  children?: JSX.Element | JSX.Element[];
};

export const BooleanField = (props: FieldProps) => {
  const { source, children } = props;
  const { record } = useRecordContext();
  if (getAttributeFromObject(record, source)) {
    return <TableCell>{children}</TableCell>;
  }
  return <TableCell></TableCell>;
};

export const LinkField = (props: LinkFieldProps) => {
  const { source, link } = props;
  const { record } = useRecordContext();

  const linkTo = useMemo(() => {
    if (typeof link === 'function') {
      return link(record);
    }
    return link;
  }, [record, link]);

  return (
    <TableCell>
      <Link to={linkTo}>{getAttributeFromObject(record, source)}</Link>
    </TableCell>
  );
};

type LinkFieldProps = FieldProps & {
  link: string | ((record?: RecordType) => string);
};

export const DateField = (props: DateFieldProps) => {
  const { record } = useRecordContext();
  const { format = 'YYYY-MM-DD HH:mm', source } = props;
  const value = getAttributeFromObject(record, source);
  return <TableCell>{value ? dayjs(value).format(format) : ''}</TableCell>;
};

type DateFieldProps = FieldProps & {
  format?: string;
};

export const ColorField = (props: FieldProps) => {
  const { source } = props;
  const { record } = useRecordContext();
  const color = getAttributeFromObject(record, source);
  return (
    <TableCell>
      <div style={{ backgroundColor: color }}>{color}</div>
    </TableCell>
  );
};

export const CheckField = (props: FieldProps) => {
  const { source } = props;
  const { record } = useRecordContext();
  return <TableCell>{getAttributeFromObject(record, source) && <Check color="success" />}</TableCell>;
};

export const EventStatusField = (props: FieldProps) => {
  const { record } = useRecordContext();
  return (
    <TableCell sx={props.hideOnMobile ? {display: {xs: 'none', md: 'table-cell'}} : undefined}>
      <EventStatusBadge status={record.status} hidden={record.hidden} />
    </TableCell>
  );
};

export const UserStatusField = (props: FieldProps) => {
  const { record } = useRecordContext();
  const blocked = Boolean(record?.block);
  const active = Boolean(record?.active);

  let label = 'Inactive';
  let color: 'default' | 'success' | 'error' = 'default';

  if (blocked) {
    label = 'Blocked';
    color = 'error';
  } else if (active) {
    label = 'Active';
    color = 'success';
  }

  return (
    <TableCell>
      <Chip size="small" label={label} color={color} variant={color === 'default' ? 'outlined' : 'filled'} />
    </TableCell>
  );
};
